import { useCallback, useEffect, useRef, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { getOrder } from '~/services/OrdersService';
import UsersService from '~/services/UsersService';

import type { ChatThreadMode, ThreadContextSnapshot } from './AiChatContext';
import { hasMeaningfulContext } from './chatThreadUtils';
import { useCurrentCustomerId } from './useCurrentCustomerId';

interface UseAdminChatContextSnapshotOptions {
  department?: string | null;
  mode?: ChatThreadMode;
}

interface ResolvedCustomer {
  orderId: string;
  customerId: string;
  customerName: string;
}

export function useAdminChatContextSnapshot({
  department = null,
  mode = 'default',
}: UseAdminChatContextSnapshotOptions = {}): ThreadContextSnapshot | null {
  const location = useLocation();
  const { customerId: urlCustomerId, orderId: urlOrderId } = useCurrentCustomerId();
  const orderCacheRef = useRef<Record<string, ResolvedCustomer>>({});
  const userNameCacheRef = useRef<Record<string, string>>({});
  const [resolvedCustomer, setResolvedCustomer] = useState<ResolvedCustomer | null>(null);
  const [contextSnapshot, setContextSnapshot] = useState<ThreadContextSnapshot | null>(null);

  useEffect(() => {
    if (!urlOrderId) {
      setResolvedCustomer(null);
      return;
    }

    const cached = orderCacheRef.current[urlOrderId];
    if (cached) {
      setResolvedCustomer(cached);
      return;
    }

    let cancelled = false;

    getOrder(urlOrderId)
      .then((response) => {
        if (cancelled) {
          return;
        }

        const order = response?.result;
        if (!order?.fk_customer_id) {
          setResolvedCustomer(null);
          return;
        }

        const result = {
          orderId: urlOrderId,
          customerId: order.fk_customer_id,
          customerName: order.customer_full_name || order.customer_email || 'Unknown',
        };
        orderCacheRef.current[urlOrderId] = result;
        setResolvedCustomer(result);
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedCustomer(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [urlOrderId]);

  const resolvedCustomerForCurrentOrder = resolvedCustomer?.orderId === urlOrderId ? resolvedCustomer : null;
  const currentCustomerId = urlCustomerId || resolvedCustomerForCurrentOrder?.customerId || null;
  const currentOrderId = urlOrderId || null;

  const resolveCustomerName = useCallback(
    async (customerId: string | null): Promise<string | null> => {
      if (!customerId) {
        return null;
      }

      if (resolvedCustomerForCurrentOrder?.customerId === customerId && resolvedCustomerForCurrentOrder.customerName) {
        return resolvedCustomerForCurrentOrder.customerName;
      }

      if (userNameCacheRef.current[customerId]) {
        return userNameCacheRef.current[customerId];
      }

      try {
        const resolvedUser = await UsersService.getUser(customerId);
        const nextName = resolvedUser?.fullName || resolvedUser?.email || 'Unknown';
        userNameCacheRef.current[customerId] = nextName;
        return nextName;
      } catch {
        return null;
      }
    },
    [resolvedCustomerForCurrentOrder?.customerId, resolvedCustomerForCurrentOrder?.customerName],
  );

  useEffect(() => {
    let cancelled = false;

    async function buildContextSnapshot() {
      const customerName =
        (await resolveCustomerName(currentCustomerId)) || resolvedCustomerForCurrentOrder?.customerName || null;
      const snapshot: ThreadContextSnapshot = {
        customerId: currentCustomerId,
        customerName,
        orderId: currentOrderId,
        department,
        mode,
        page: location.pathname || null,
      };

      if (!cancelled) {
        setContextSnapshot(hasMeaningfulContext(snapshot) ? snapshot : null);
      }
    }

    void buildContextSnapshot().catch(() => {
      if (!cancelled) {
        setContextSnapshot(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    currentCustomerId,
    currentOrderId,
    department,
    location.pathname,
    mode,
    resolveCustomerName,
    resolvedCustomerForCurrentOrder?.customerName,
  ]);

  return contextSnapshot;
}
